/* eslint-disable @typescript-eslint/no-explicit-any */
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useServices } from "@/services/hooks/useServices";
import { useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  AlertCircle,
  BarChart,
  CheckCircle,
  ChevronRight,
  Clock,
  Info,
  Loader2,
  Mail,
  Server,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { TerminalLog } from "./terminal-log";

interface TestProgressAnimationProps {
  testId: string;
  senderEmail: string;
  testType: string;
  provider: string[];
  onComplete?: (testData: any) => void;
  onCompleteShowReport?: boolean;
}

interface Update {
  type: string;
  status: string;
  step: string;
  message: string;
  details?: {
    provider?: string;
    status?: string;
  };
  progress?: number;
  timestamp?: number;
  account?: string;
}

export function TestProgressAnimation({
  testId,
  onComplete,
  senderEmail,
  testType,
  provider,
  onCompleteShowReport = true,
}: TestProgressAnimationProps) {
  const [updates, setUpdates] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isCompleted, setIsCompleted] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<string>("initializing");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [completedCount, setCompletedCount] = useState<number>(0);
  const [fakeCompletedCount, setFakeCompletedCount] = useState<number>(0);
  const { sseService } = useServices();
  const [timeRemaining, setTimeRemaining] = useState(5 * 60); // 5 minutes in seconds
  const [lastCompletedCountUpdate, setLastCompletedCountUpdate] =
    useState<number>(Date.now());

  // Add this new state for active tab management
  const [activeTab, setActiveTab] = useState<
    "overview" | "activity" | "results"
  >("overview");

  // Add navigate function to route to the report page
  const navigate = useNavigate();

  // Memoize the deduplicateUpdates function
  const deduplicateUpdates = useCallback((updates: Update[]): Update[] => {
    const uniqueUpdates: Update[] = [];
    const seen = new Set();

    for (const update of updates) {
      // Only process updates that have a server timestamp
      if (!update.timestamp) continue;

      const key = `${update.step}-${update.message}-${
        update.details?.provider || ""
      }-${update.timestamp}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniqueUpdates.push(update);
      }
    }

    // Sort by server timestamp
    return uniqueUpdates.sort((a, b) => a.timestamp! - b.timestamp!);
  }, []); // Empty dependency array since this function never needs to change

  // Add this helper function at the top of your component
  const getRequiredCompletions = (testType: string) => {
    if (Array.isArray(provider) && provider.length > 0) {
      return provider.length * 5;
    }

    switch (testType.toLowerCase()) {
      case "b2b":
        return 40;
      case "b2c":
        return 25;
      case "all":
        return 65;
      default:
        return 65; // default to 'all' threshold
    }
  };

  // Add this helper function in your component
  const getTestSummary = () => {
    const requiredCompletions = getRequiredCompletions(testType);
    const completed = Math.min(fakeCompletedCount, requiredCompletions); // Ensure we don't exceed total
    const pending = requiredCompletions - completed;
    const percentComplete = (completed / requiredCompletions) * 100;

    return {
      total: requiredCompletions,
      completed,
      pending,
      percentComplete,
    };
  };

  // Add effect to monitor completion count updates
  useEffect(() => {
    const checkInterval = setInterval(() => {
      const timeSinceLastUpdate = Date.now() - lastCompletedCountUpdate;

      // If no updates for 1 minutes (60000ms), trigger completion
      if (timeSinceLastUpdate > 60000 && !isCompleted && completedCount >= 5) {
        setIsCompleted(true);
        if (onComplete) {
          onComplete({
            status: "completed",
            message: "Test completed due to inactivity",
            details: { testId },
          });
        }
        sseService.disconnectFromTestStream(testId);
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(checkInterval);
  }, [lastCompletedCountUpdate, isCompleted, onComplete, sseService, testId]);

  useEffect(() => {
    let mounted = true;

    const initializeConnection = async () => {
      try {
        // Get previous updates
        const previousUpdates = await sseService.fetchTestUpdates(testId);
        if (mounted) {
          // Add timestamps to previous updates if they don't have them
          const updatesWithTimestamps = previousUpdates.map(
            (update: Update) => ({
              ...update,
              timestamp: update.timestamp || Date.now(),
            })
          );

          // Deduplicate and set updates
          const dedupedUpdates = deduplicateUpdates(updatesWithTimestamps);
          setUpdates(dedupedUpdates);
          setIsLoading(false);

          // Count how many completed items we already have using the deduped updates
          const initialCompletedCount = dedupedUpdates.filter(
            (update: any) =>
              update.type === "completion" && update.step === "test_completed"
          ).length;
          setCompletedCount(initialCompletedCount);
          setFakeCompletedCount(initialCompletedCount);

          // Get required completions based on test type
          const requiredCompletions = getRequiredCompletions(testType);

          // Check if test is already completed based on test type
          const isAlreadyCompleted =
            initialCompletedCount >= requiredCompletions || timeRemaining <= 0;

          if (!isAlreadyCompleted) {
            // Connect to SSE for live updates
            await sseService.connectToTestStream(testId, {
              onOpen: () => {
                if (mounted) setIsConnected(true);
              },
              onMessage: (data) => {
                if (mounted && data?.data) {
                  setUpdates((prev) => {
                    if (Array.isArray(data.data)) {
                      return deduplicateUpdates([...prev, ...data.data]);
                    } else {
                      return deduplicateUpdates([...prev, data.data]);
                    }
                  });
                }
              },
              onError: () => {
                if (mounted) setIsConnected(false);
              },
            });
          } else if (onComplete) {
            // Trigger completion callback if test is already done
            const finalUpdate = dedupedUpdates[dedupedUpdates.length - 1];
            setIsCompleted(true);
            onComplete(finalUpdate);
          }
        }
      } catch (error) {
        console.error("Failed to initialize test progress:", error);
        if (mounted) {
          setIsLoading(false);
          setError("Failed to load test progress. Please try again.");
        }
      }
    };

    initializeConnection();

    return () => {
      mounted = false;
      sseService.disconnectFromTestStream(testId);
    };
  }, [testId, sseService, onComplete, testType, deduplicateUpdates]);

  // Process updates to calculate progress and current step
  useEffect(() => {
    if (updates.length === 0) return;

    // Get latest update
    const latestUpdate = updates[updates.length - 1];

    // Update progress
    if (latestUpdate.progress !== undefined) {
      setProgress(latestUpdate.progress);
    }

    // Update current step
    if (latestUpdate.step) {
      setCurrentStep(latestUpdate.step);
    }

    // Count the number of completion updates
    const newCompletedCount = updates.filter(
      (update) => update.status === "completed" && update.type === "completion"
    ).length;

    const requiredCompletions = getRequiredCompletions(testType);

    if (newCompletedCount !== completedCount) {
      setLastCompletedCountUpdate(Date.now());
      // Ensure completedCount doesn't exceed required completions
      const boundedCompletedCount = Math.min(
        newCompletedCount,
        requiredCompletions
      );
      setCompletedCount(boundedCompletedCount);

      // Update fake count with at most one additional completion, not exceeding required completions
      const newFakeCount = Math.min(
        boundedCompletedCount + 2,
        requiredCompletions
      );

      setFakeCompletedCount(newFakeCount);
    }

    // Get test summary
    const summary = getTestSummary();

    // Check if all tests are completed (pending is 0)
    if (summary.pending === 0 && !isCompleted) {
      setIsCompleted(true);

      // Show success toast
      toast.success("All tests completed successfully!", {
        description: `${
          summary.total
        } tests completed for ${testType.toUpperCase()} providers`,
      });

      // Call onComplete to close dialog
      if (onComplete) {
        onComplete({
          status: "completed",
          message: "All tests completed successfully",
          details: { testId },
        });
      }

      // Navigate to the report page
      if (onCompleteShowReport) {
        setTimeout(() => {
          navigate({
            to: `/placement-test/${senderEmail}`,
            search: {
              type: testType.toUpperCase(),
              _testId: testId,
            },
          });
        }, 1500); // Short delay to allow the toast to be seen
      }

      // Disconnect SSE
      sseService.disconnectFromTestStream(testId);
    }

    // Existing error handling
    if (latestUpdate.status === "failed") {
      setIsCompleted(true);
      setError(latestUpdate.message || "Test failed");
      if (onComplete) {
        onComplete(latestUpdate);
      }
      sseService.disconnectFromTestStream(testId);
    }
  }, [
    updates,
    onComplete,
    isCompleted,
    sseService,
    testId,
    testType,
    completedCount,
    navigate,
    senderEmail,
  ]);

  // Get list of providers that have been processed
  const getProcessedProviders = () => {
    // Check if updates is an array before calling filter
    if (!Array.isArray(updates)) {
      return []; // Return empty array if updates is not an array
    }

    return updates
      .filter(
        (update) => update?.type === "delivery" && update?.details?.provider
      )
      .map((update) => update?.details?.provider)
      .filter((provider, index, self) => self.indexOf(provider) === index);
  };

  const processedProviders = getProcessedProviders();

  // Get the status of each step
  const getStepStatus = (stepId: string) => {
    const stepUpdates = updates.filter((update) => update.step === stepId);

    // If stepId is "test_completed", only mark it as completed if we have 3+ completion records
    if (stepId === "test_completed") {
      if (completedCount >= 3) return "completed";
      if (completedCount > 0) return "in_progress"; // Partial completion
      return "pending";
    }

    // For other steps, use the original logic
    if (stepUpdates.length > 0) {
      return stepUpdates[stepUpdates.length - 1].status === "failed"
        ? "failed"
        : "completed";
    }

    // Check if it's the current step
    if (currentStep === stepId) return "in_progress";

    // Check if we're past this step
    const stepIndex = testSteps.findIndex((step) => step.id === stepId);
    const currentStepIndex = testSteps.findIndex(
      (step) => step.id === currentStep
    );

    if (stepIndex < currentStepIndex) return "completed";

    return "pending";
  };

  // Add a new function to show partial completion for the test_completed step
  const renderStepIcon = (step: string, status: string) => {
    // For test_completed step with partial completion
    if (step === "test_completed" && status === "in_progress") {
      return (
        <div className="rounded-full bg-yellow-500 text-white p-1 flex items-center justify-center w-6 h-6">
          <CheckCircle className="h-4 w-4" />
        </div>
      );
    }

    // Default rendering based on status
    if (status === "completed") {
      return (
        <div className="rounded-full bg-green-500 text-white p-1 flex items-center justify-center w-6 h-6">
          <CheckCircle className="h-4 w-4" />
        </div>
      );
    } else if (status === "failed") {
      return (
        <div className="rounded-full bg-red-500 text-white p-1 flex items-center justify-center w-6 h-6">
          <XCircle className="h-4 w-4" />
        </div>
      );
    } else if (status === "in_progress") {
      return (
        <div className="rounded-full bg-primary text-white p-1 flex items-center justify-center w-6 h-6">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      );
    } else {
      return (
        <div className="rounded-full border border-muted-foreground text-muted-foreground p-1 flex items-center justify-center w-6 h-6">
          <div className="h-4 w-4" />
        </div>
      );
    }
  };

  // Render step icon based on step name for the activity log
  const renderActivityIcon = (step: string) => {
    switch (step) {
      case "sending_email":
        return <Mail className="h-3.5 w-3.5 text-blue-400" />;
      case "mx_check":
        return <AlertCircle className="h-3.5 w-3.5 text-yellow-400" />;
      case "blacklist_check":
        return <AlertCircle className="h-3.5 w-3.5 text-orange-400" />;
      case "email_received":
        return <CheckCircle className="h-3.5 w-3.5 text-green-400" />;
      case "test_completed":
        return <CheckCircle className="h-3.5 w-3.5 text-green-400" />;
      default:
        return <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />;
    }
  };

  // Define common test steps that need to be shown
  const testSteps = [
    { id: "setup", label: "Setting up test", icon: "⚙️" },
    { id: "creating_test", label: "Creating placement test", icon: "🔧" },
    { id: "sending_email", label: "Sending test emails", icon: "📧" },
    { id: "email_received", label: "Emails delivered", icon: "✉️" },
    { id: "mx_check", label: "Checking MX records", icon: "🔍" },
    { id: "blacklist_check", label: "Verifying blacklist status", icon: "🛡️" },
    { id: "test_completed", label: "Test completed", icon: "✅" },
  ];

  useEffect(() => {
    // Set 15 minute timeout (15 * 60 * 1000 milliseconds)
    const timeoutDuration = 15 * 60 * 1000;

    const timeoutId = setTimeout(() => {
      // Force completion and close dialog
      setIsCompleted(true);
      if (onComplete) {
        onComplete({
          status: "completed",
          message: "Test timed out after 15 minutes",
          details: { testId },
        });
      }

      // Disconnect SSE
      sseService.disconnectFromTestStream(testId);
    }, timeoutDuration);

    // Cleanup timeout if component unmounts or test completes normally
    return () => {
      clearTimeout(timeoutId);
    };
  }, [testId, onComplete, sseService]); // Dependencies

  // Update time remaining every second
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) return 0;
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Update time remaining based on progress
  useEffect(() => {
    const summary = getTestSummary();
    const percentComplete = summary.percentComplete;

    // Calculate expected remaining time based on progress
    const totalTime = 5 * 60; // 5 minutes in seconds
    const expectedRemaining = Math.ceil(
      (totalTime * (100 - percentComplete)) / 100
    );

    // Only update if the difference is significant (more than 5 seconds)
    if (Math.abs(timeRemaining - expectedRemaining) > 5) {
      setTimeRemaining(expectedRemaining);
    }
  }, [fakeCompletedCount]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return (
    <Card className="w-full p-4 h-[80vh] overflow-hidden shadow-lg border-zinc-200 dark:border-zinc-800">
      {isLoading ? (
        <div className="h-full flex flex-col items-center justify-center space-y-4">
          <div className="relative">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <motion.div
              className="absolute inset-0 rounded-full border-t-2 border-primary"
              animate={{ rotate: 360 }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
            Preparing your delivery test...
          </p>
          <Progress value={30} className="h-1.5 w-48 animate-pulse" />
        </div>
      ) : (
        <div className="grid grid-cols-1 h-full">
          {/* Header section with test info and status */}
          <div className="border-b border-zinc-200 dark:border-zinc-800 p-4 bg-white dark:bg-zinc-950">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-full">
                  <Server className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">
                    Inbox Placement Test
                  </h2>
                  <p className="text-sm text-muted-foreground">{testId}</p>
                </div>
              </div>
              <Badge
                variant={
                  completedCount >= 3
                    ? error
                      ? "destructive"
                      : "success"
                    : completedCount > 0
                    ? "secondary"
                    : isConnected
                    ? "default"
                    : "outline"
                }
                className="py-1 px-3 text-xs capitalize"
              >
                {completedCount >= 3
                  ? error
                    ? "Failed"
                    : "Partially Completed"
                  : completedCount > 0
                  ? "Partially Completed"
                  : isConnected
                  ? "Running"
                  : "Connecting..."}
              </Badge>
            </div>

            {/* Test info cards */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="flex items-center gap-2 p-3 bg-zinc-50 dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
                <Mail className="h-4 w-4 text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">Sender</div>
                  <div className="text-sm font-medium truncate">
                    {senderEmail}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-zinc-50 dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
                <BarChart className="h-4 w-4 text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">Test Type</div>
                  <div className="text-sm font-medium capitalize">
                    {testType === "all" ? "B2B & B2C" : testType.toUpperCase()}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-zinc-50 dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
                <Clock className="h-4 w-4 text-zinc-500" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">Time Left</div>
                  <div
                    className={`text-sm font-medium font-mono ${
                      timeRemaining < 60
                        ? "text-red-500"
                        : timeRemaining < 180
                        ? "text-amber-500"
                        : ""
                    }`}
                  >
                    {formatTime(timeRemaining)}
                  </div>
                </div>
                <div className="h-1.5 w-12 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-1000 ${
                      timeRemaining < 60
                        ? "bg-red-500"
                        : timeRemaining < 180
                        ? "bg-amber-500"
                        : "bg-primary"
                    }`}
                    style={{
                      width: `${(timeRemaining / (15 * 60)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Main progress bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <div className="text-sm font-medium">
                  {getTestSummary().completed} of {getTestSummary().total} tests
                  complete
                </div>
                <div className="text-sm font-medium text-primary">
                  {Math.round(getTestSummary().percentComplete)}%
                </div>
              </div>
              <div className="h-2.5 bg-zinc-100 dark:bg-zinc-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary"
                  initial={{ width: "0%" }}
                  animate={{ width: `${getTestSummary().percentComplete}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {error ||
                  (updates.length > 0
                    ? updates[updates.length - 1]?.message
                    : "Initializing test...")}
              </p>
            </div>
          </div>

          {/* Content section with tabs */}
          <div className="overflow-hidden flex flex-col">
            <div className="flex border-b border-zinc-200 dark:border-zinc-800">
              <button
                onClick={() => setActiveTab("overview")}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "overview"
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab("activity")}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "activity"
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }`}
              >
                Activity Log
              </button>
              <button
                onClick={() => setActiveTab("results")}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === "results"
                    ? "text-primary border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground transition-colors"
                }`}
              >
                Detailed Results
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
              {/* Overview Tab Content */}
              {activeTab === "overview" && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="col-span-2 space-y-4">
                    {/* Progress stats */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="flex flex-col items-center p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-100 dark:border-green-900">
                        <div className="text-2xl font-semibold text-green-600 dark:text-green-400">
                          {getTestSummary().completed}
                        </div>
                        <div className="text-xs text-green-600 dark:text-green-400 font-medium">
                          Completed
                        </div>
                      </div>

                      <div className="flex flex-col items-center p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-100 dark:border-amber-900">
                        <div className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
                          {getTestSummary().pending}
                        </div>
                        <div className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                          Pending
                        </div>
                      </div>

                      <div className="flex flex-col items-center p-4 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-900">
                        <div className="text-2xl font-semibold text-blue-600 dark:text-blue-400">
                          {getTestSummary().total}
                        </div>
                        <div className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                          Total
                        </div>
                      </div>
                    </div>

                    {/* Step progression */}
                    <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                      <h3 className="text-sm font-medium mb-3">
                        Test Progress Steps
                      </h3>
                      <div className="space-y-3">
                        {testSteps.map((step, index) => {
                          const status = getStepStatus(step.id);
                          const stepColor =
                            status === "completed"
                              ? "text-green-500 dark:text-green-400"
                              : status === "in_progress"
                              ? "text-primary"
                              : status === "failed"
                              ? "text-red-500"
                              : "text-zinc-400";

                          return (
                            <motion.div
                              key={step.id}
                              initial={{ opacity: 0, y: 5 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="flex items-center"
                            >
                              <div className="mr-3 relative">
                                {index !== 0 && (
                                  <div
                                    className={`absolute top-[-16px] left-1/2 w-0.5 h-4 -translate-x-1/2 ${
                                      getStepStatus(testSteps[index - 1].id) ===
                                      "completed"
                                        ? "bg-green-500 dark:bg-green-400"
                                        : "bg-zinc-200 dark:bg-zinc-700"
                                    }`}
                                  />
                                )}
                                {renderStepIcon(step.id, status)}
                              </div>
                              <div className="flex-1">
                                <div
                                  className={`text-sm font-medium ${stepColor}`}
                                >
                                  {step.label}
                                </div>
                                {status === "in_progress" && (
                                  <p className="text-xs text-muted-foreground mt-0.5">
                                    {updates.find((u) => u.step === step.id)
                                      ?.message || "Processing..."}
                                  </p>
                                )}
                              </div>
                              <div className="ml-2">
                                {status === "completed" && (
                                  <CheckCircle className="h-4 w-4 text-green-500 dark:text-green-400" />
                                )}
                                {status === "failed" && (
                                  <XCircle className="h-4 w-4 text-red-500" />
                                )}
                                {status === "in_progress" && (
                                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Right sidebar with providers */}
                  <div className="space-y-4">
                    {/* Current activity card */}
                    <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                      <h3 className="text-sm font-medium mb-3">
                        Current Activity
                      </h3>
                      <div className="space-y-2">
                        {updates
                          .slice(-3)
                          .reverse()
                          .map((update, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <div
                                className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                                  update.status === "completed"
                                    ? "bg-green-500"
                                    : update.status === "failed"
                                    ? "bg-red-500"
                                    : "bg-amber-500"
                                }`}
                              />
                              <p className="text-xs text-zinc-600 dark:text-zinc-400 line-clamp-2">
                                {update.message}
                              </p>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Providers card */}
                    {processedProviders.length > 0 && (
                      <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                        <h3 className="text-sm font-medium mb-3">
                          Delivered To
                        </h3>
                        <div className="grid grid-cols-2 gap-2">
                          {processedProviders.map((provider) => (
                            <Badge
                              key={provider}
                              variant="secondary"
                              className="py-1 text-center justify-center"
                            >
                              {provider}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tip card */}
                    <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-900 p-4">
                      <div className="flex items-start gap-3">
                        <Info className="h-5 w-5 text-blue-500 dark:text-blue-400 mt-0.5 shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-400">
                            Testing in Progress
                          </h4>
                          <p className="text-xs text-blue-600/80 dark:text-blue-500 mt-1">
                            Tests automatically complete after sufficient data
                            is collected or after timeout.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity Log Tab Content */}
              {activeTab === "activity" && (
                <div className="h-full">
                  <div className="h-[550px]">
                    <TerminalLog updates={updates} testSteps={testSteps} />
                  </div>
                </div>
              )}

              {/* Detailed Results Tab Content */}
              {activeTab === "results" && (
                <div className="space-y-4">
                  <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                    <h3 className="text-sm font-medium mb-3">
                      Test Results Summary
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Total Providers Tested:</div>
                        <div className="text-sm font-medium">
                          {getTestSummary().total}
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Tests Completed:</div>
                        <div className="text-sm font-medium text-green-600">
                          {getTestSummary().completed}
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Tests Pending:</div>
                        <div className="text-sm font-medium text-amber-600">
                          {getTestSummary().pending}
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="text-sm">Completion Rate:</div>
                        <div className="text-sm font-medium">
                          {Math.round(getTestSummary().percentComplete)}%
                        </div>
                      </div>
                    </div>
                  </div>

                  {processedProviders.length > 0 && (
                    <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4">
                      <h3 className="text-sm font-medium mb-3">
                        Provider Delivery Results
                      </h3>
                      <div className="grid grid-cols-1 gap-2">
                        {processedProviders.map((provider) => (
                          <div
                            key={provider}
                            className="flex items-center justify-between p-2 bg-zinc-50 dark:bg-zinc-900 rounded border border-zinc-200 dark:border-zinc-800"
                          >
                            <span className="text-sm font-medium">
                              {provider}
                            </span>
                            <Badge variant="success" className="capitalize">
                              Delivered
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Error message display */}
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 rounded-lg border border-red-100 dark:border-red-900 text-sm"
                    >
                      <div className="font-medium flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Error Detected
                      </div>
                      <div className="mt-1 pl-6">{error}</div>
                    </motion.div>
                  )}
                </div>
              )}

              {/* Show activity log mini view only on Overview tab */}
              {activeTab === "overview" && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium">
                      Detailed Activity Log
                    </h3>
                    <button
                      onClick={() => setActiveTab("activity")}
                      className="text-xs text-primary flex items-center"
                    >
                      View full log <ChevronRight className="h-3 w-3 ml-1" />
                    </button>
                  </div>
                  <div className="h-[250px]">
                    <TerminalLog updates={updates} testSteps={testSteps} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
